import {
  globalHistory,
  navigate,
  useLocation
} from '@reach/router'
import {
  useEffect,
  useState
} from 'react'
import useConfirm from './useConfirm'

interface Props {
  isOffPrompt?: boolean
  onLeaveWithSubmit?: (newUrl: string) => void
  onLeaveWithoutSubmit?: (newUrl: string) => void
  confirmationMessage?: string
  actionText?: string
  cancelText?: string
  title?: string
}

const alertUserClose = (event: BeforeUnloadEvent) => {
  event.preventDefault()

  // eslint-disable-next-line fp/no-mutation
  event.returnValue = '' // to ensure the event prompt for user input(reload or leave) and return blank value as we no need evaluate the value meaning
}

const PreventNavigate = ({
  isOffPrompt: initIsOffPromt,
  onLeaveWithSubmit,
  onLeaveWithoutSubmit,
  confirmationMessage = 'You\'re about to leave this page with unsaved changes. Save before continuing?',
  actionText,
  cancelText,
  title
}: Props) => {
  const [isFirstConfirm, setIsFirstConfirm] = useState(true)
  const [navTo, setNavTo] = useState('')

  const handleReset = () => {
    setNavTo('')
    setIsFirstConfirm(true)
  }

  const {
    getConfirmation,
    isLeaveWithSave,
    Confirmation
  } = useConfirm(handleReset, actionText, cancelText, title)

  const currentRoute = useLocation().pathname

  // to handle browser level event like tab close, brower close
  useEffect(() => {
    window.addEventListener('beforeunload', alertUserClose)

    return () => {
      window.removeEventListener('beforeunload', alertUserClose)
    }
  })

  // to handle any redirection happen on the URL address changes
  useEffect(() =>
  // to confirm the event is fully unsubscribe and not carry to other page
    globalHistory.listen(async ({
      action,
      location
    }) => {
      const isUserLeaving =
        (!initIsOffPromt &&
        action === 'PUSH' && // detected when new URL is added
        location.pathname !== currentRoute
        ) || action === 'POP' // detected when browser back is clicked

      if (isUserLeaving) {
        if (isFirstConfirm) {
          setIsFirstConfirm(false)
          void navigate(currentRoute)
          setNavTo(location.pathname)

          await getConfirmation(confirmationMessage)
        }
      }
    })
  , [currentRoute, getConfirmation, initIsOffPromt, isFirstConfirm, onLeaveWithSubmit, onLeaveWithoutSubmit, confirmationMessage])

  useEffect(() => {
    if (!isFirstConfirm && typeof isLeaveWithSave === 'boolean') {
      if (isLeaveWithSave && onLeaveWithSubmit) {
        onLeaveWithSubmit(navTo)

        return
      }

      if (!isLeaveWithSave && onLeaveWithoutSubmit) {
        onLeaveWithoutSubmit(navTo)

        return
      }

      setNavTo('')
      setIsFirstConfirm(true)
    }
  }
  , [isFirstConfirm, isLeaveWithSave, navTo, onLeaveWithSubmit, onLeaveWithoutSubmit])

  return <Confirmation />
}

export default PreventNavigate
